import { useMemo, useState, useCallback } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  CalendarDays,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  CreditCard,
  FileText,
  Filter,
  HandCoins,
  Landmark,
  PiggyBank,
  Receipt,
  RefreshCw,
  Search,
  Send,
  SlidersHorizontal,
  TrendingUp,
  Users,
  Wallet,
  AlertCircle,
} from "lucide-react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { DateRange } from "react-day-picker";
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, subDays, subMonths } from "date-fns";
import { ColumnDef } from "@tanstack/react-table";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/auth.context";
import { PageTransition } from "@/components/PageTransition";
import { DateFilter } from "@/components/dashboard";
import type { DateFilterValue } from "@/components/dashboard";
import DataTable from "@/components/datatable/Datatable";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const formatMoney = (value: number | string | undefined | null) =>
  `${Number(value || 0).toLocaleString()} FRW`;

const formatNumber = (value: number | string | undefined | null) =>
  Number(value || 0).toLocaleString();

const iconTone: Record<string, string> = {
  green: "bg-[#0A9F43]/10 text-[#0A9F43]",
  blue: "bg-blue-100 text-blue-600",
  purple: "bg-purple-100 text-purple-600",
  amber: "bg-amber-100 text-amber-600",
  orange: "bg-orange-100 text-orange-600",
  red: "bg-red-100 text-red-500",
};

function getDisplayName(user: any): string {
  return user?.fullName || user?.name || user?.names || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Admin";
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  tone = "green",
  loading,
  footer,
  onClick,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  tone?: keyof typeof iconTone;
  loading?: boolean;
  footer?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all hover:shadow-md">
      <CardContent className="flex min-h-[180px] flex-col p-5">
        <div className="flex items-start gap-3">
          <div className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", iconTone[tone])}>
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
            {loading ? (
              <Skeleton className="mt-3 h-8 w-32" />
            ) : (
              <p className="mt-2 text-2xl font-bold tracking-tight text-[#0b0e2a]">{value}</p>
            )}
            {subtitle && <p className="mt-1 text-xs text-gray-400">{subtitle}</p>}
          </div>
        </div>
        {footer && <div className="mt-auto pt-3 border-t border-gray-100">{footer}</div>}
        {onClick && (
          <button
            onClick={onClick}
            className="mt-auto flex w-fit items-center gap-1.5 rounded-lg bg-[#0A9F43]/5 px-3 py-1.5 text-xs font-semibold text-[#0A9F43] transition hover:bg-[#0A9F43]/10"
          >
            View Details <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function PanelHeader({
  title,
  subtitle,
  icon,
  action,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-gray-600">{icon}</div>
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wide text-[#0b0e2a]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function SnapshotItem({
  label,
  value,
  icon,
  tone = "green",
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: keyof typeof iconTone;
}) {
  return (
    <div className="min-w-0 border-gray-100 py-2 sm:border-r sm:pr-5 last:border-r-0">
      <div className="flex items-center gap-2">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", iconTone[tone])}>
          {icon}
        </span>
        <p className="truncate text-xs font-semibold text-gray-500">{label}</p>
      </div>
      <p className={cn("mt-3 text-base font-bold", tone === "red" ? "text-red-600" : tone === "amber" || tone === "orange" ? "text-orange-600" : tone === "blue" ? "text-blue-700" : tone === "purple" ? "text-purple-700" : "text-[#0A9F43]")}>
        {value}
      </p>
    </div>
  );
}

function LoanStatus({
  label,
  value,
  note,
  icon,
  tone = "green",
}: {
  label: string;
  value: string | number;
  note: string;
  icon: React.ReactNode;
  tone?: keyof typeof iconTone;
}) {
  return (
    <div className="min-w-0 border-gray-100 py-2 md:border-r md:pr-5 last:border-r-0">
      <div className="flex items-center gap-2">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", iconTone[tone])}>
          {icon}
        </span>
        <p className="truncate text-xs font-semibold text-gray-500">{label}</p>
      </div>
      <p className="mt-3 text-2xl font-bold text-[#0b0e2a]">{value}</p>
      <p className="mt-1 text-xs text-gray-400">{note}</p>
    </div>
  );
}

export default function DashboardOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState<DateFilterValue>("today");
  const [customRange, setCustomRange] = useState<DateRange | undefined>();

  const {
    data: rawData,
    status,
    error,
    refetch,
  } = useQuery(
    "full-dashboard",
    async () => {
      const { data } = await api.get("/reports/dashboard/full");
      return data?.data;
    },
    { staleTime: 30000, cacheTime: 60000, retry: 1, refetchOnWindowFocus: false },
  );

  const { data: pendingPayments } = useQuery(
    "pending-member-payments-count",
    async () => {
      const { data } = await api.get("/member-payments?status=pending_approval&limit=1");
      return { count: data?.count || data?.data?.length || 0, total: data?.total || 0 };
    },
    { staleTime: 15000, retry: 1 },
  );

  const { data: pendingLoans } = useQuery(
    "pending-loans-count",
    async () => {
      const { data } = await api.get("/loans?status=pending&limit=1");
      return { count: data?.count || 0 };
    },
    { staleTime: 15000, retry: 1 },
  );

  const { data: pendingTickets } = useQuery(
    "pending-tickets-count",
    async () => {
      const { data } = await api.get("/admin/support-tickets?status=open&limit=1");
      return { count: data?.count || data?.data?.length || 0 };
    },
    { staleTime: 15000, retry: 1 },
  );

  const selectedRange = useMemo(() => {
    const today = new Date();
    if (dateFilter === "custom" && customRange?.from) {
      return {
        start: startOfDay(customRange.from),
        end: endOfDay(customRange.to || customRange.from),
      };
    }
    switch (dateFilter) {
      case "yesterday": {
        const y = subDays(today, 1);
        return { start: startOfDay(y), end: endOfDay(y) };
      }
      case "last7":
        return { start: startOfDay(subDays(today, 6)), end: endOfDay(today) };
      case "last30":
        return { start: startOfDay(subDays(today, 29)), end: endOfDay(today) };
      case "thisMonth":
        return { start: startOfMonth(today), end: endOfDay(today) };
      case "lastMonth": {
        const lm = subMonths(today, 1);
        return { start: startOfMonth(lm), end: endOfMonth(lm) };
      }
      default:
        return { start: startOfDay(today), end: endOfDay(today) };
    }
  }, [dateFilter, customRange]);

  const rangeLabel = useMemo(
    () => `${format(selectedRange.start, "MMM d, yyyy")} - ${format(selectedRange.end, "MMM d, yyyy")}`,
    [selectedRange],
  );

  const stats = rawData?.stats || {};
  const allContributions = rawData?.recentContributions || [];
  const recentContributions = allContributions.slice(0, 5);
  const recentLoanPayments = (rawData?.recentLoanPayments || []).slice(0, 5);
  const recentExpenses = (rawData?.recentExpenses || []).slice(0, 5);
  const memberStats = rawData?.memberStats || {};

  const activeMembers = Number(stats?.activeMembers || memberStats?.activeMembers || 0);
  const totalMembers = Number(stats?.totalMembers || memberStats?.totalMembers || activeMembers || 0);
  const inactiveMembers = Math.max(totalMembers - activeMembers, Number(memberStats?.inactiveThisMonth || 0));
  const totalFunds = Number(stats?.totalSavings || 0) + Number(stats?.totalContributions || 0);
  const activeLoans = Number(stats?.activeLoans || 0);
  const pendingLoanCount = Number(pendingLoans?.count || 0);
  const completedLoans = Number(stats?.completedLoans || stats?.paidLoans || 0);
  const overdueLoans = Number(stats?.overdueLoans || stats?.defaultedLoans || 0);
  const loanBalance = Number(stats?.loansBalance || 0);
  const interestEarned = Number(stats?.totalInterest || 0);
  const totalDisbursed = Number(stats?.totalDisbursed || 0);
  const expenses = Number(stats?.totalExpenses || 0);
  const distributableBalance = Math.max(interestEarned - expenses, 0);
  const totalPending = Number(pendingPayments?.count || 0) + pendingLoanCount + Number(pendingTickets?.count || 0);

  const activityItems = useMemo(() => {
    const contributionItems = recentContributions.map((item: any) => ({
      time: item.date || item.createdAt || item.contributionDate,
      title: "Contribution approved",
      subject: item.memberName || item.member?.name || "Member",
      amount: Number(item.amount || 0),
      icon: <HandCoins className="h-3.5 w-3.5" />,
      tone: "green" as const,
    }));
    const loanItems = recentLoanPayments.map((item: any) => ({
      time: item.date || item.createdAt || item.paymentDate,
      title: "Loan repayment recorded",
      subject: item.memberName || item.member?.name || "Member",
      amount: Number(item.amount || item.paidAmount || 0),
      icon: <Receipt className="h-3.5 w-3.5" />,
      tone: "orange" as const,
    }));
    const expenseItems = recentExpenses.map((item: any) => ({
      time: item.date || item.createdAt || item.expenseDate,
      title: "Expense recorded",
      subject: item.categoryName || item.description || "Expense",
      amount: Number(item.amount || 0),
      icon: <CreditCard className="h-3.5 w-3.5" />,
      tone: "red" as const,
    }));
    return [...contributionItems, ...loanItems, ...expenseItems]
      .sort((a, b) => new Date(b.time || 0).getTime() - new Date(a.time || 0).getTime())
      .slice(0, 5);
  }, [recentContributions, recentExpenses, recentLoanPayments]);

  const distributionRows = useMemo(() => {
    const fallbackMembers = recentContributions.map((item: any, index: number) => ({
      member: item.memberName || item.member?.name || `Member ${index + 1}`,
      shares: Number(item.shareCount || item.shares || 0),
      amount: Number(item.amount || 0),
    }));
    const rows = (rawData?.interestDistribution || rawData?.topMembers || fallbackMembers).slice(0, 5);
    const totalShares = rows.reduce((sum: number, row: any) => sum + Number(row.shares || row.shareCount || 0), 0) || Number(stats?.totalShares || 0) || 1;
    return rows.map((row: any, index: number) => {
      const shares = Number(row.shares || row.shareCount || 0);
      const sharePercent = totalShares > 0 ? (shares / totalShares) * 100 : 0;
      const estimatedDividend = Number(row.estimatedDividend || row.dividend || 0) || Math.round(distributableBalance * (sharePercent / 100));
      return {
        rank: index + 1,
        member: row.member || row.memberName || row.name || `Member ${index + 1}`,
        shares,
        sharePercent,
        estimatedDividend,
      };
    });
  }, [distributableBalance, rawData?.interestDistribution, rawData?.topMembers, recentContributions, stats?.totalShares]);

  const contributionColumns: ColumnDef<any, any>[] = useMemo(() => [
    {
      accessorKey: "teacher",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Member" />,
      cell: ({ row }) => {
        const teacher = row.original.teacher;
        const name = teacher?.fullName || row.original.memberName || "Unknown";
        return <span className="font-medium text-gray-900">{name}</span>;
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => (
        <span className="text-gray-700">{Number(row.original.amount).toLocaleString()} FRW</span>
      ),
    },
    {
      accessorKey: "contributionDate",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const date = row.original.contributionDate || row.original.date;
        return (
          <span className="text-xs text-gray-500">
            {date ? new Date(date).toLocaleDateString() : "-"}
          </span>
        );
      },
    },
    {
      accessorKey: "contributionType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => {
        const source = row.original.source;
        const type = row.original.contributionType || row.original.type || "Monthly";
        const displayType = source === "salary_deduction" ? "Salary Deduction"
          : source === "manual_member_contribution" ? "Manual"
          : type;
        return <span className="text-xs text-gray-500">{displayType}</span>;
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const isVerified = row.original.isVerified;
        const status = row.original.status;
        let displayStatus = "Pending";
        let colorClass = "bg-amber-50 text-amber-700";
        if (status === "Rejected") {
          displayStatus = "Rejected";
          colorClass = "bg-red-50 text-red-700";
        } else if (isVerified || status === "Verified" || status === "Completed") {
          displayStatus = "Verified";
          colorClass = "bg-green-50 text-green-700";
        }
        return (
          <span className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
            colorClass,
          )}>
            {displayStatus}
          </span>
        );
      },
    },
  ], []);

  const [contribPageIndex, setContribPageIndex] = useState(0);
  const [contribPageSize, setContribPageSize] = useState(10);

  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [filterAmountMin, setFilterAmountMin] = useState<string>("");
  const [filterAmountMax, setFilterAmountMax] = useState<string>("");
  const [filterMember, setFilterMember] = useState<string>("");

  const [viewOpen, setViewOpen] = useState(false);
  const [draftType, setDraftType] = useState(filterType);
  const [draftDateFrom, setDraftDateFrom] = useState(filterDateFrom);
  const [draftDateTo, setDraftDateTo] = useState(filterDateTo);
  const [draftAmountMin, setDraftAmountMin] = useState(filterAmountMin);
  const [draftAmountMax, setDraftAmountMax] = useState(filterAmountMax);

  const buildContributionFilters = useCallback(() => {
    const filters: any[] = [];
    if (filterStatus === "verified") {
      filters.push({ field: "isVerified", operator: "eq", value: true });
    } else if (filterStatus === "pending") {
      filters.push({ field: "isVerified", operator: "eq", value: false });
    } else if (filterStatus === "rejected") {
      filters.push({ field: "status", operator: "eq", value: "Rejected" });
    }
    if (filterType !== "all") {
      if (filterType === "salary_deduction") {
        filters.push({ field: "source", operator: "eq", value: "salary_deduction" });
      } else if (filterType === "manual") {
        filters.push({ field: "source", operator: "eq", value: "manual_member_contribution" });
      } else {
        filters.push({ field: "contributionType", operator: "eq", value: filterType });
      }
    }
    if (filterDateFrom) {
      filters.push({ field: "contributionDate", operator: "gte", value: filterDateFrom });
    }
    if (filterDateTo) {
      filters.push({ field: "contributionDate", operator: "lte", value: filterDateTo });
    }
    if (filterAmountMin) {
      filters.push({ field: "amount", operator: "gte", value: Number(filterAmountMin) });
    }
    if (filterAmountMax) {
      filters.push({ field: "amount", operator: "lte", value: Number(filterAmountMax) });
    }
    return filters;
  }, [filterStatus, filterType, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax]);

  const applyAdvancedFilters = useCallback(() => {
    setFilterType(draftType);
    setFilterDateFrom(draftDateFrom);
    setFilterDateTo(draftDateTo);
    setFilterAmountMin(draftAmountMin);
    setFilterAmountMax(draftAmountMax);
    setContribPageIndex(0);
    setViewOpen(false);
  }, [draftType, draftDateFrom, draftDateTo, draftAmountMin, draftAmountMax]);

  const { data: contribData, isLoading: contribLoading } = useQuery(
    ["dashboard-contributions", contribPageIndex, contribPageSize, filterStatus, filterType, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, filterMember],
    async () => {
      const filters = buildContributionFilters();
      const params = new URLSearchParams();
      params.set("page", String(contribPageIndex + 1));
      params.set("limit", String(contribPageSize));
      if (filters.length > 0) {
        params.set("filters", JSON.stringify(filters));
      }
      if (filterMember.trim()) {
        params.set("search", filterMember.trim());
      }
      params.set("sortBy", "contributionDate");
      params.set("order", "DESC");
      const { data } = await api.get(`/contributions?${params.toString()}`);
      return data;
    },
    { staleTime: 15000, retry: 1 },
  );

  const contribResults = contribData?.results || [];
  const contribTotal = contribData?.total || 0;
  const contribTotalPages = contribData?.totalPages || Math.max(1, Math.ceil(contribTotal / contribPageSize));
  const loading = status === "loading";

  if (status === "error") {
    return (
      <PageTransition>
        <div className="flex min-h-[70vh] items-center justify-center p-6">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
            <h2 className="mb-2 text-lg font-semibold text-gray-900">Failed to load dashboard</h2>
            <p className="mb-4 text-sm text-gray-500">
              {(error as any)?.response?.data?.message || (error as any)?.message || "Could not connect to the server."}
            </p>
            <Button onClick={() => refetch()} variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50">
              <RefreshCw className="h-4 w-4" /> Retry
            </Button>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-slate-50/40">
        <div className="space-y-4 min-w-0">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-[#0b0e2a]">Dashboard</h1>
                <p className="mt-1 text-sm font-semibold text-gray-600">
                  Welcome back, {getDisplayName(user)}
                </p>
                <p className="mt-1 text-sm text-gray-400">Here is what is happening in IKIMINA today.</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <DateFilter
                  value={dateFilter}
                  onChange={setDateFilter}
                  rangeLabel={rangeLabel}
                  customRange={customRange}
                  onCustomRangeChange={setCustomRange}
                />
              </div>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <MetricCard
              title="Members"
              value={formatNumber(totalMembers)}
              subtitle="Total registered members"
              icon={<Users className="h-5 w-5" />}
              tone="green"
              loading={loading}
              footer={
                <div className="flex flex-wrap gap-4 text-xs">
                  <span className="font-semibold text-[#0A9F43]">{formatNumber(activeMembers)} Active</span>
                  <span className="font-semibold text-red-500">{formatNumber(inactiveMembers)} Inactive</span>
                </div>
              }
            />
            <MetricCard
              title="Members' Total Funds"
              value={formatMoney(totalFunds)}
              subtitle="Total savings + contributions"
              icon={<Wallet className="h-5 w-5" />}
              tone="blue"
              loading={loading}
              onClick={() => navigate("/reports/financial")}
            />
            <MetricCard
              title="Loan Portfolio"
              value={`${formatNumber(activeLoans)} ${activeLoans === 1 ? "Loan" : "Loans"}`}
              subtitle={`${formatMoney(loanBalance)} outstanding balance`}
              icon={<CreditCard className="h-5 w-5" />}
              tone="purple"
              loading={loading}
              onClick={() => navigate("/loans")}
            />
            <MetricCard
              title="IKIMINA Main Account"
              value={formatMoney(distributableBalance)}
              subtitle="Balance to be distributed"
              icon={<Landmark className="h-5 w-5" />}
              tone="amber"
              loading={loading}
              footer={
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-400">Interest Earned</span>
                    <span className="font-bold text-[#0A9F43]">{formatMoney(interestEarned)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-400">Expenses</span>
                    <span className="font-bold text-red-500">{formatMoney(expenses)}</span>
                  </div>
                </div>
              }
            />
            <MetricCard
              title="Pending Approvals"
              value={totalPending}
              subtitle="Awaiting review"
              icon={<ClipboardCheck className="h-5 w-5" />}
              tone="orange"
              loading={loading}
              footer={
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-400">Contributions</span><span className="font-bold text-blue-600">{pendingPayments?.count ?? 0}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Loans</span><span className="font-bold text-blue-600">{pendingLoanCount}</span></div>
                  <div className="flex justify-between"><span className="text-gray-400">Tickets</span><span className="font-bold text-blue-600">{pendingTickets?.count ?? 0}</span></div>
                </div>
              }
            />
          </section>

          <section className="grid gap-4 xl:grid-cols-2">
            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="p-5 pb-2">
                <PanelHeader
                  icon={<TrendingUp className="h-5 w-5 text-blue-600" />}
                  title="Today's Financial Snapshot"
                  subtitle="Key transactions recorded today"
                />
              </CardHeader>
              <CardContent className="grid gap-3 p-5 pt-2 sm:grid-cols-2 lg:grid-cols-4">
                <SnapshotItem label="Contributions" value={formatMoney(stats?.currentMonthSavings || stats?.todayContributions || 0)} icon={<HandCoins className="h-4 w-4" />} tone="green" />
                <SnapshotItem label="Savings" value={formatMoney(stats?.totalSavings || 0)} icon={<PiggyBank className="h-4 w-4" />} tone="blue" />
                <SnapshotItem label="Loan Disbursements" value={formatMoney(totalDisbursed || stats?.totalDisbursed || 0)} icon={<Banknote className="h-4 w-4" />} tone="purple" />
                <SnapshotItem label="Loan Repayments" value={formatMoney(stats?.totalRepaid || 0)} icon={<Receipt className="h-4 w-4" />} tone="orange" />
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="p-5 pb-2">
                <PanelHeader
                  icon={<Users className="h-5 w-5 text-gray-600" />}
                  title="Loan Overview"
                  subtitle="Current status of IKIMINA loan portfolio"
                  action={
                    <button onClick={() => navigate("/loans")} className="flex items-center gap-1 text-xs font-semibold text-[#0A9F43] hover:underline">
                      View All Loans <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              </CardHeader>
              <CardContent className="grid gap-3 p-5 pt-2 sm:grid-cols-2 lg:grid-cols-4">
                <LoanStatus label="Active Loans" value={activeLoans} note={formatMoney(loanBalance)} icon={<Users className="h-4 w-4" />} tone="green" />
                <LoanStatus label="Pending" value={pendingLoanCount} note="Under review" icon={<FileText className="h-4 w-4" />} tone="blue" />
                <LoanStatus label="Completed" value={completedLoans} note="Fully paid" icon={<CheckCircle2 className="h-4 w-4" />} tone="purple" />
                <LoanStatus label="Overdue" value={overdueLoans} note="Requires attention" icon={<AlertCircle className="h-4 w-4" />} tone="red" />
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-4 xl:grid-cols-3">
            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] xl:col-span-1">
              <CardHeader className="p-5 pb-2">
                <PanelHeader
                  icon={<BadgeCheck className="h-5 w-5 text-gray-600" />}
                  title="Interest Distribution"
                  subtitle="Estimated distribution based on current shares"
                  action={
                    <div className="rounded-lg bg-[#0A9F43]/5 px-3 py-2 text-right">
                      <p className="text-[10px] font-semibold uppercase text-[#0A9F43]">Available</p>
                      <p className="text-sm font-bold text-[#0A9F43]">{formatMoney(distributableBalance)}</p>
                    </div>
                  }
                />
              </CardHeader>
              <CardContent className="p-5 pt-2">
                <div className="overflow-hidden rounded-lg border border-gray-100">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-semibold">#</th>
                        <th className="px-3 py-2 font-semibold">Member</th>
                        <th className="px-3 py-2 font-semibold">Share</th>
                        <th className="px-3 py-2 text-right font-semibold">Dividend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <tr key={index} className="border-t border-gray-100">
                            <td colSpan={4} className="px-3 py-2"><Skeleton className="h-4 w-full" /></td>
                          </tr>
                        ))
                      ) : distributionRows.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-8 text-center text-gray-400">No distribution data yet</td></tr>
                      ) : (
                        distributionRows.map((row) => (
                          <tr key={`${row.rank}-${row.member}`} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-400">{row.rank}</td>
                            <td className="px-3 py-2 font-semibold text-gray-800">{row.member}</td>
                            <td className="px-3 py-2 text-gray-600">{row.sharePercent.toFixed(2)}%</td>
                            <td className="px-3 py-2 text-right font-bold text-[#0b0e2a]">{formatMoney(row.estimatedDividend)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <button onClick={() => navigate("/interest-distribution")} className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#0A9F43] hover:underline">
                  View Full Interest Distribution <ArrowRight className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="p-5 pb-2">
                <PanelHeader
                  icon={<Clock className="h-5 w-5 text-gray-600" />}
                  title="Recent Activities"
                  subtitle="Latest activities in IKIMINA"
                />
              </CardHeader>
              <CardContent className="space-y-3 p-5 pt-2">
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => <Skeleton key={index} className="h-10 w-full" />)
                ) : activityItems.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-400">No recent activities yet</p>
                ) : (
                  activityItems.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="flex items-center gap-3">
                      <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", iconTone[item.tone])}>
                        {item.icon}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold text-gray-800">{item.title}</p>
                        <p className="truncate text-[11px] text-gray-400">{item.subject}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-[#0b0e2a]">{item.amount ? formatMoney(item.amount) : "-"}</p>
                        <p className="text-[10px] text-gray-300">{item.time ? format(new Date(item.time), "HH:mm") : "--:--"}</p>
                      </div>
                    </div>
                  ))
                )}
                <button onClick={() => navigate("/audit-logs")} className="mx-auto flex items-center gap-2 text-sm font-semibold text-[#0A9F43] hover:underline">
                  View All Activities <ArrowRight className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>

            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="p-5 pb-2">
                <PanelHeader
                  icon={<CalendarDays className="h-5 w-5 text-gray-600" />}
                  title="Upcoming Meetings"
                  subtitle="Scheduled meetings and events"
                  action={
                    <Button size="sm" className="h-8 bg-[#0A9F43] text-xs font-semibold hover:bg-[#0A9F43]/90" onClick={() => navigate("/meetings")}>
                      Create Meeting
                    </Button>
                  }
                />
              </CardHeader>
              <CardContent className="space-y-3 p-5 pt-2">
                {[
                  { title: "Board Meeting", date: "Next scheduled session", audience: "Board Members", tone: "green" as const },
                  { title: "General Assembly", date: "Monthly member meeting", audience: "All Members", tone: "purple" as const },
                ].map((meeting) => (
                  <div key={meeting.title} className="flex items-center gap-3 rounded-lg border border-gray-100 p-3">
                    <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", iconTone[meeting.tone])}>
                      <CalendarDays className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900">{meeting.title}</p>
                      <p className="text-xs text-gray-400">{meeting.date}</p>
                    </div>
                    <span className="rounded-md bg-gray-50 px-2 py-1 text-[11px] font-semibold text-gray-500">{meeting.audience}</span>
                  </div>
                ))}
                <button onClick={() => navigate("/meetings")} className="mx-auto flex items-center gap-2 text-sm font-semibold text-[#0A9F43] hover:underline">
                  View All Meetings <ArrowRight className="h-4 w-4" />
                </button>
              </CardContent>
            </Card>
          </section>

          <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
              {[
                { label: "Add Member", icon: Users, path: "/members" },
                { label: "Record Contribution", icon: HandCoins, path: "/contributions" },
                { label: "Record Saving", icon: PiggyBank, path: "/share-declaration" },
                { label: "Issue Loan", icon: CreditCard, path: "/loans/apply" },
                { label: "Record Repayment", icon: Receipt, path: "/loan-repayments" },
                { label: "Create Meeting", icon: CalendarDays, path: "/meetings" },
                { label: "Send Notification", icon: Send, path: "/support-tickets" },
                { label: "Generate Statement", icon: FileText, path: "/statements" },
              ].map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-3 text-left text-xs font-semibold text-gray-600 transition hover:border-[#0A9F43]/30 hover:bg-[#0A9F43]/5 hover:text-[#0A9F43]"
                >
                  <action.icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 leading-snug">{action.label}</span>
                </button>
              ))}
            </CardContent>
          </Card>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            {/* Toolbar: Search | Status chip | spacer | View */}
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative w-full sm:w-[320px] sm:flex-shrink-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Search members by name, email, phone..."
                  value={filterMember}
                  onChange={(e) => { setFilterMember(e.target.value); setContribPageIndex(0); }}
                  className="h-10 w-full pl-8 pr-3 text-sm"
                />
              </div>

              {/* Status chip */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "inline-flex items-center gap-1.5 h-10 rounded-lg border px-3 text-sm font-medium transition-colors",
                      filterStatus !== "all"
                        ? "border-[#0A9F43] bg-[#0A9F43]/5 text-[#0A9F43]"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <Filter className="h-3.5 w-3.5" />
                    Status
                    {filterStatus !== "all" && (
                      <span className="ml-0.5 rounded-full bg-[#0A9F43]/10 px-1.5 text-[10px] font-semibold leading-tight">
                        1
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" sideOffset={4} className="w-44 p-1">
                  {[
                    { value: "all", label: "All Status" },
                    { value: "pending", label: "Pending" },
                    { value: "verified", label: "Verified" },
                    { value: "rejected", label: "Rejected" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setFilterStatus(opt.value); setContribPageIndex(0); }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors",
                        filterStatus === opt.value
                          ? "bg-[#0A9F43]/10 text-[#0A9F43] font-medium"
                          : "text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      {filterStatus === opt.value && <Check className="h-3.5 w-3.5" />}
                      <span className={filterStatus !== opt.value ? "ml-[22px]" : ""}>{opt.label}</span>
                    </button>
                  ))}
                </PopoverContent>
              </Popover>

              {/* Spacer */}
              <div className="flex-1" />

              {/* View button */}
              <Popover
                open={viewOpen}
                onOpenChange={(open) => {
                  setViewOpen(open);
                  if (open) {
                    setDraftType(filterType);
                    setDraftDateFrom(filterDateFrom);
                    setDraftDateTo(filterDateTo);
                    setDraftAmountMin(filterAmountMin);
                    setDraftAmountMax(filterAmountMax);
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    className={cn(
                      "inline-flex items-center gap-1.5 h-10 rounded-lg border px-3 text-sm font-medium transition-colors",
                      (filterType !== "all" || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax)
                        ? "border-[#0A9F43] bg-[#0A9F43]/5 text-[#0A9F43]"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    View
                    {(filterType !== "all" || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax) && (
                      <span className="ml-0.5 rounded-full bg-[#0A9F43]/10 px-1.5 text-[10px] font-semibold leading-tight">
                        {[filterType !== "all", filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax].filter(Boolean).length}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={4} className="w-80 p-0">
                  <div className="border-b px-4 py-3">
                    <p className="text-sm font-semibold text-[#0b0e2a]">Advanced Filters</p>
                  </div>
                  <div className="flex flex-col gap-3 p-4">
                    {/* Type */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-gray-500">Type</label>
                      <select
                        value={draftType}
                        onChange={(e) => setDraftType(e.target.value)}
                        className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:border-[#0A9F43] focus:outline-none"
                      >
                        <option value="all">All Types</option>
                        <option value="Monthly">Monthly</option>
                        <option value="salary_deduction">Salary Deduction</option>
                        <option value="manual">Manual</option>
                        <option value="Weekly">Weekly</option>
                        <option value="One-time">One-time</option>
                      </select>
                    </div>

                    {/* Date range */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-gray-500">From Date</label>
                        <Input
                          type="date"
                          value={draftDateFrom}
                          onChange={(e) => setDraftDateFrom(e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-gray-500">To Date</label>
                        <Input
                          type="date"
                          value={draftDateTo}
                          onChange={(e) => setDraftDateTo(e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>

                    {/* Amount range */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-gray-500">Min Amount</label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={draftAmountMin}
                          onChange={(e) => setDraftAmountMin(e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-gray-500">Max Amount</label>
                        <Input
                          type="number"
                          placeholder="No limit"
                          value={draftAmountMax}
                          onChange={(e) => setDraftAmountMax(e.target.value)}
                          className="h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between border-t px-4 py-3">
                    <button
                      onClick={() => {
                        setDraftType("all");
                        setDraftDateFrom("");
                        setDraftDateTo("");
                        setDraftAmountMin("");
                        setDraftAmountMax("");
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Reset
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setViewOpen(false)}
                        className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={applyAdvancedFilters}
                        className="rounded-md bg-[#0A9F43] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#088838]"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <DataTable
              columns={contributionColumns}
              data={contribResults}
              isLoading={contribLoading}
              isFetching={contribLoading}
              pageIndex={contribPageIndex}
              pageSize={contribPageSize}
              pageCount={contribTotalPages}
              setPagination={(updater) => {
                const newState = typeof updater === "function"
                  ? updater({ pageIndex: contribPageIndex, pageSize: contribPageSize })
                  : updater;
                setContribPageIndex(newState.pageIndex);
                setContribPageSize(newState.pageSize);
              }}
              facets={[]}
              showSearch={false}
              emptyMessage="No contributions found"
              emptyDescription="Try adjusting your filters or search terms."
              enableExport={false}
              hidePagination={false}
            />
          </section>
        </div>
      </div>
    </PageTransition>
  );
}
